import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { CreateFlightPlanInput } from "@/api/v1/flight-plan-types";
import { PolygonGeometry } from "@/api/v1/geofence-types";
import * as turf from "@turf/turf";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CalendarIcon, Plane, User, FileText, Clock, MapPin } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

function calculatePolygonAreaKm2(polygon: PolygonGeometry): number {
  try {
    if (!polygon || polygon.length === 0 || !polygon[0] || polygon[0].length < 3) {
      return 0;
    }
    
    // Convert to GeoJSON Polygon format for turf
    const geoJsonPolygon: GeoJSON.Polygon = {
      type: "Polygon",
      coordinates: polygon,
    };
    
    // Calculate area in square meters, then convert to km²
    const areaM2 = turf.area(geoJsonPolygon);
    return areaM2 / 1000000; // Convert m² to km²
  } catch (error) {
    console.error("Error calculating polygon area:", error);
    return 0;
  }
}

// Create schema factory that accepts polygon for validation
const createFlightPlanFormSchema = (polygon: PolygonGeometry) => {
  const now = new Date();
  const maxAdvanceDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days from now
  const minDuration = 5 * 60 * 1000; // 5 minutes in milliseconds
  const maxDuration = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
  
  return z.object({
    // Required fields
    purpose: z.string().min(1, "Purpose is required"),
    min_altitude_agl_ft: z.coerce.number()
      .min(0, "Min altitude must be >= 0"),
    max_altitude_agl_ft: z.coerce.number()
      .min(0, "Max altitude must be >= 0")
      .max(400, "Max altitude must be <= 400 ft AGL"),
    starts_at: z.date({ required_error: "Start time is required" })
      .refine((date) => date > now, "Start time must be in the future"),
    ends_at: z.date({ required_error: "End time is required" })
      .refine((date) => date > now, "End time must be in the future"),
    
    // Optional operator details
    operator_id: z.string().optional().or(z.literal("")),
    operator_contact_name: z.string().optional().or(z.literal("")),
    operator_contact_email: z.string().email("Invalid email").optional().or(z.literal("")),
    operator_contact_phone: z.string().optional().or(z.literal("")),
    
    // Optional pilot details
    flyer_id: z.string().optional().or(z.literal("")),
    pilot_contact_name: z.string().optional().or(z.literal("")),
    pilot_contact_email: z.string().email("Invalid email").optional().or(z.literal("")),
    pilot_contact_phone: z.string().optional().or(z.literal("")),
    
    // Optional additional info
    a2cofc_url: z.string().url("Must be a valid URL").optional().or(z.literal("")),
    number_aircraft: z.coerce.number().min(1, "Must be at least 1").optional().or(z.literal("")),
    takeoff_location: z.string().optional().or(z.literal("")),
    additional_information: z.string().optional().or(z.literal("")),
  })
  .refine(
    (data) => data.max_altitude_agl_ft >= data.min_altitude_agl_ft,
    {
      message: "Max altitude must be >= Min altitude",
      path: ["max_altitude_agl_ft"],
    }
  )
  .refine(
    (data) => data.ends_at > data.starts_at,
    {
      message: "End time must be after start time",
      path: ["ends_at"],
    }
  )
  .refine(
    (data) => {
      const duration = data.ends_at.getTime() - data.starts_at.getTime();
      return duration >= minDuration && duration <= maxDuration;
    },
    {
      message: "Flight duration must be between 5 minutes and 8 hours",
      path: ["ends_at"],
    }
  )
  .refine(
    (data) => data.starts_at <= maxAdvanceDate,
    {
      message: "Flight cannot be scheduled more than 30 days in advance",
      path: ["starts_at"],
    }
  )
  .refine(
    () => {
      // Validate polygon has at least 3 points
      if (!polygon || polygon.length === 0 || !polygon[0]) {
        return false;
      }
      return polygon[0].length >= 3;
    },
    {
      message: "Polygon must have at least 3 points",
      path: ["purpose"], // Attach to a field so it shows up
    }
  )
  .refine(
    () => {
      // Validate polygon area <= 1000 km²
      const areaKm2 = calculatePolygonAreaKm2(polygon);
      return areaKm2 > 0 && areaKm2 <= 1000;
    },
    {
      message: "Polygon area must be <= 1000 km²",
      path: ["purpose"], // Attach to a field so it shows up
    }
  );
};

interface FlightPlanFormProps {
  onSubmit: (data: CreateFlightPlanInput) => void;
  polygon: PolygonGeometry;
  isSubmitting?: boolean;
}

export function FlightPlanForm({
  onSubmit,
  polygon,
  isSubmitting = false,
}: FlightPlanFormProps) {
  const flightPlanFormSchema = createFlightPlanFormSchema(polygon);
  type FlightPlanFormValues = z.infer<typeof flightPlanFormSchema>;
  
  const form = useForm<FlightPlanFormValues>({
    resolver: zodResolver(flightPlanFormSchema),
    defaultValues: {
      min_altitude_agl_ft: 0,
      max_altitude_agl_ft: 400,
      purpose: "",
      operator_id: "",
      operator_contact_name: "",
      operator_contact_email: "",
      operator_contact_phone: "",
      flyer_id: "",
      pilot_contact_name: "",
      pilot_contact_email: "",
      pilot_contact_phone: "",
      a2cofc_url: "",
      takeoff_location: "",
      additional_information: "",
    },
  });

  // Validate polygon and get area for display
  const polygonAreaKm2 = calculatePolygonAreaKm2(polygon);
  const polygonPointCount = polygon?.[0]?.length || 0;
  const polygonErrors: string[] = [];
  
  if (polygonPointCount < 3) {
    polygonErrors.push("Polygon must have at least 3 points");
  }
  if (polygonAreaKm2 === 0) {
    polygonErrors.push("Polygon area is invalid");
  } else if (polygonAreaKm2 > 1000) {
    polygonErrors.push(`Polygon area (${polygonAreaKm2.toFixed(2)} km²) exceeds maximum of 1000 km²`);
  }

  const handleSubmit = (values: FlightPlanFormValues) => {
    const payload: CreateFlightPlanInput = {
      // Required fields
      polygon_zone: polygon,
      min_altitude_agl_ft: values.min_altitude_agl_ft,
      max_altitude_agl_ft: values.max_altitude_agl_ft,
      starts_at: values.starts_at.toISOString(),
      ends_at: values.ends_at.toISOString(),
      purpose: values.purpose,
      // Optional fields - convert empty strings to undefined
      operator_id: values.operator_id || undefined,
      operator_contact_name: values.operator_contact_name || undefined,
      operator_contact_email: values.operator_contact_email || undefined,
      operator_contact_phone: values.operator_contact_phone || undefined,
      flyer_id: values.flyer_id || undefined,
      pilot_contact_name: values.pilot_contact_name || undefined,
      pilot_contact_email: values.pilot_contact_email || undefined,
      pilot_contact_phone: values.pilot_contact_phone || undefined,
      a2cofc_url: values.a2cofc_url || undefined,
      number_aircraft: typeof values.number_aircraft === 'number' ? values.number_aircraft : undefined,
      takeoff_location: values.takeoff_location || undefined,
      additional_information: values.additional_information || undefined,
    };
    onSubmit(payload);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        {/* Polygon Validation Errors */}
        {polygonErrors.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="font-semibold mb-1">Polygon Validation Errors:</div>
              <ul className="list-disc list-inside space-y-1">
                {polygonErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        
        {/* Polygon Area Info */}
        {polygonAreaKm2 > 0 && polygonAreaKm2 <= 1000 && (
          <Alert>
            <AlertDescription>
              Flight area: {polygonAreaKm2.toFixed(2)} km² ({polygonPointCount} points)
            </AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
                <div className="bg-primary/10 p-2 rounded-full">
                    <Plane className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold">Flight Information</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                    <FormField
                        control={form.control}
                        name="purpose"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Purpose of Flight <span className="text-destructive">*</span></FormLabel>
                            <FormControl>
                            <Input placeholder="e.g. Survey, Inspection" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />

                    <div className="grid grid-cols-2 gap-4">
                        <FormField
                            control={form.control}
                            name="min_altitude_agl_ft"
                            render={({ field }) => (
                            <FormItem>
                                <FormLabel>Min Altitude (ft) <span className="text-destructive">*</span></FormLabel>
                                <FormControl>
                                <Input type="number" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="max_altitude_agl_ft"
                            render={({ field }) => (
                            <FormItem>
                                <FormLabel>Max Altitude (ft) <span className="text-destructive">*</span></FormLabel>
                                <FormControl>
                                <Input type="number" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                            )}
                        />
                    </div>

                    <FormField
                        control={form.control}
                        name="number_aircraft"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Number of Aircraft</FormLabel>
                            <FormControl>
                            <Input type="number" placeholder="1" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                    <FormField
                        control={form.control}
                        name="takeoff_location"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Takeoff Location</FormLabel>
                            <FormControl>
                            <div className="relative">
                                <MapPin className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                <Input placeholder="///word.word.word" className="pl-8" {...field} />
                            </div>
                            </FormControl>
                            <FormDescription>what3words address (optional)</FormDescription>
                            <FormMessage />
                        </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="a2cofc_url"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>A2 CofC URL</FormLabel>
                            <FormControl>
                            <Input placeholder="https://..." {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                </div>
            </div>
        </div>

        <Separator />

        <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
                <div className="bg-primary/10 p-2 rounded-full">
                    <Clock className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold">Schedule</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <FormField
                  control={form.control}
                  name="starts_at"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Start Time <span className="text-destructive">*</span></FormLabel>
                      <div className="flex gap-2">
                        <Popover>
                            <PopoverTrigger asChild>
                            <FormControl>
                                <Button
                                variant={"outline"}
                                className={cn(
                                    "w-full pl-3 text-left font-normal flex-1",
                                    !field.value && "text-muted-foreground"
                                )}
                                >
                                {field.value ? (
                                    format(field.value, "PPP")
                                ) : (
                                    <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                            </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={(date) => {
                                    if (!date) return;
                                    const newDate = new Date(date);
                                    if (field.value) {
                                        newDate.setHours(field.value.getHours());
                                        newDate.setMinutes(field.value.getMinutes());
                                    } else {
                                        newDate.setHours(12, 0);
                                    }
                                    field.onChange(newDate);
                                }}
                                disabled={(date) =>
                                date < new Date(new Date().setHours(0, 0, 0, 0))
                                }
                                initialFocus
                            />
                            </PopoverContent>
                        </Popover>
                        <Input
                            type="time"
                            className="w-[120px]"
                            value={field.value ? format(field.value, "HH:mm") : ""}
                            onChange={(e) => {
                                const date = field.value || new Date();
                                const [hours, minutes] = e.target.value.split(':');
                                const newDate = new Date(date);
                                newDate.setHours(parseInt(hours), parseInt(minutes));
                                field.onChange(newDate);
                            }}
                        />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="ends_at"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>End Time <span className="text-destructive">*</span></FormLabel>
                      <div className="flex gap-2">
                        <Popover>
                            <PopoverTrigger asChild>
                            <FormControl>
                                <Button
                                variant={"outline"}
                                className={cn(
                                    "w-full pl-3 text-left font-normal flex-1",
                                    !field.value && "text-muted-foreground"
                                )}
                                >
                                {field.value ? (
                                    format(field.value, "PPP")
                                ) : (
                                    <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                            </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={(date) => {
                                    if (!date) return;
                                    const newDate = new Date(date);
                                    if (field.value) {
                                        newDate.setHours(field.value.getHours());
                                        newDate.setMinutes(field.value.getMinutes());
                                    } else {
                                        newDate.setHours(13, 0);
                                    }
                                    field.onChange(newDate);
                                }}
                                disabled={(date) =>
                                date < new Date(new Date().setHours(0, 0, 0, 0))
                                }
                                initialFocus
                            />
                            </PopoverContent>
                        </Popover>
                        <Input
                            type="time"
                            className="w-[120px]"
                            value={field.value ? format(field.value, "HH:mm") : ""}
                            onChange={(e) => {
                                const date = field.value || new Date();
                                const [hours, minutes] = e.target.value.split(':');
                                const newDate = new Date(date);
                                newDate.setHours(parseInt(hours), parseInt(minutes));
                                field.onChange(newDate);
                            }}
                        />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
            </div>
        </div>

        <Separator />

        <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
                <div className="bg-primary/10 p-2 rounded-full">
                    <User className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold">Personnel</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Operator Details */}
                <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
                    <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Operator Details (Optional)</h4>
                    <FormField
                        control={form.control}
                        name="operator_id"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Operator ID</FormLabel>
                            <FormControl>
                            <Input placeholder="e.g. GBR-OP-XXXXX" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="operator_contact_name"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Contact Name</FormLabel>
                            <FormControl>
                            <Input {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="operator_contact_email"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Contact Email</FormLabel>
                            <FormControl>
                            <Input type="email" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="operator_contact_phone"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Contact Phone</FormLabel>
                            <FormControl>
                            <Input {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                </div>

                {/* Pilot Details */}
                <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
                    <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Pilot Details (Optional)</h4>
                    <FormField
                        control={form.control}
                        name="flyer_id"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Flyer ID</FormLabel>
                            <FormControl>
                            <Input {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="pilot_contact_name"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Pilot Name</FormLabel>
                            <FormControl>
                            <Input {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="pilot_contact_email"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Pilot Email</FormLabel>
                            <FormControl>
                            <Input type="email" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="pilot_contact_phone"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Pilot Phone</FormLabel>
                            <FormControl>
                            <Input {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                </div>
            </div>
        </div>

        <Separator />

        <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
                <div className="bg-primary/10 p-2 rounded-full">
                    <FileText className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold">Additional Information</h3>
            </div>
            
            <FormField
            control={form.control}
            name="additional_information"
            render={({ field }) => (
                <FormItem>
                <FormControl>
                    <Textarea
                    placeholder="Any other relevant details or notes for the flight plan..."
                    className="min-h-[100px]"
                    {...field}
                    />
                </FormControl>
                <FormMessage />
                </FormItem>
            )}
            />
        </div>

        <div className="pt-4">
            <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-lg">
            {isSubmitting ? (
                <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Submitting Flight Plan...
                </>
            ) : (
                "Submit Flight Plan"
            )}
            </Button>
        </div>
      </form>
    </Form>
  );
}
